import { API_URL } from './env.ts';
import { randomText } from './portal-flow.ts';
import type { AuthLoginResponse } from '../types/auth.ts';
import type { ApiResponse } from '../types/api-response.ts';

/**
 * SSO 受け口専用の fetch（api-client は使わない）。
 *
 * 既存サブアプリの教訓:
 *   api-client.ts の request() は 401 で /login にフルリダイレクトするため、
 *   SSO 交換失敗時の理由が画面に出せなくなる。よって SSO 専用にプレーンな fetch を使う。
 *
 * - exchangePortalCode  … 引換番号（2026-10-21〜）。番号と合言葉を POST /auth/portal-code に出す
 * 旧方式（ポータルの7日の証明書 portal_token を POST /auth/portal に出す）は 2026-10-22 に消した。
 * 失敗時はそのまま例外を上に伝える（URL のポータルの値は、読み込んだ時点で lib/portal-arrival.ts が消している）。
 */

export type PortalLoginError = {
  code: string;
  message: string;
};

export class PortalLoginException extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'PortalLoginException';
  }
}

const postAuth = async (path: string, body: unknown): Promise<AuthLoginResponse> => {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const json = (await res.json().catch(() => null)) as ApiResponse<AuthLoginResponse> | null;
  if (json && 'ok' in json && json.ok === true) return json.data;

  const errorRes = json as { error?: PortalLoginError } | null;
  throw new PortalLoginException(
    errorRes?.error?.code ?? 'unknown',
    errorRes?.error?.message ?? 'portal login failed'
  );
};

/** 引換番号でのログイン（2026-10-21〜） */
export const exchangePortalCode = (code: string, verifier: string): Promise<AuthLoginResponse> =>
  postAuth('/auth/portal-code', { code, verifier });

/**
 * このブラウザで始めていない番号（戻るボタン・人から送られたリンク）を、でたらめの合言葉で出して使い切る。
 * ポータルは合言葉が違っても番号を使用済みにする。番号がほかで使われる前に消すため、結果は待たない
 */
export const burnPortalCode = (code: string): void => {
  void fetch(`${API_URL}/auth/portal-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, verifier: randomText() }),
  }).catch(() => {});
};
